import { useMutation } from "@apollo/client"
import { Cancel, Edit, Save } from "@mui/icons-material"
import {
  Alert,
  Backdrop,
  Box,
  Card,
  CardHeader,
  CircularProgress,
  Dialog,
  IconButton,
  InputAdornment,
  Paper,
  Snackbar,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material"
import { StyledButton } from "components/Button/StyledButton"
import { HAUFE_COMPANY_ID } from "config"
import { DELETE_UNUSED_EXAMINATIONS } from "graphql/queries"
import {
  Device,
  DeviceAux,
  DeviceEutGroup,
  DeviceType,
  Lead,
  Project,
  SalesQuote,
  Status,
  User,
} from "graphql/types"
import { useStoreActions, useStoreState } from "hooks/storeHooks"
import { useEffect, useRef, useState } from "react"
import { useLocation, useParams } from "react-router-dom"
import { emptyPerson } from "store/store"
import { isDeviceAux, isDeviceEut, isEutGroup } from "store/typeGuards"
import { formatDateTime } from "utils/dateTimeUtils"
import { getOperatingModes } from "utils/getOperatingModes"
import { useLocalStorage } from "utils/useLocalStorage"
import { v4 } from "uuid"
import { DeviceDetails } from "./DeviceDetails"
import { ProjectDetailGeneralData } from "./ProjectDetailGeneralData"
import { ProjectDeviceList } from "./ProjectDeviceList"
import { TestSetupPlanTab, TestSetupPlanTabRef } from "./TestSetupPlanTab"

const emptyProject: Project = {
  id: "",
  name: "",
  lead: {
    id: "",
    opportunityID: "",
    businessAccount: "",
    status: "",
    ownerEmployeeName: "",
  },
  description: "",
  specialArrangements: "",
  desiredDate: "",
  plannedDate: "",
  updatedDate: "",
  contacts: [],
  devices: [],
  management: emptyPerson,
  qtp: [],
  sales: emptyPerson,
  documents: [],
  notes: "",
}

export const ProjectDetail = () => {
  const [currentUser] = useLocalStorage<User>("user", emptyPerson)
  const { projectID } = useParams()
  const location = useLocation()
  const { projects, leads, customers, employees } = useStoreState((state) => state)
  const {
    updateProject,
    getSalesQuotesByOpportunityIDs,
    updateEutGroup,
    updateAux,
    updateExamination,
    fetchLeads,
    fetchCustomers,
    fetchEmployees,
    fetchProjects,
  } = useStoreActions((action) => action)
  const [salesQuote, setSalesQuote] = useState<SalesQuote | null>(null)
  const [project, setProject] = useState<Project>(emptyProject)
  const [currentLead, setCurrentLead] = useState<Lead | null>(null)
  const testSetupPlanTabRef = useRef<TestSetupPlanTabRef>(null)
  const [editTitle, setEditTitle] = useState<string>("")
  const [selectedDevice, setSelectedDevice] = useState<Device | null>(null)
  const [isEditing, setIsEditing] = useState(false)
  const [currentTab, setCurrentTab] = useState(0)
  const [hasTestSetupChanges, setHasTestSetupChanges] = useState(false)
  const [deleteUnusedExaminations] = useMutation(DELETE_UNUSED_EXAMINATIONS)

  const [snackbar, setSnackbar] = useState<{
    message: string
    severity: "success" | "error"
  } | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isUpdating, setIsUpdating] = useState(false)

  const getPersonBySalesQuote = (salesQuote: SalesQuote) => {
    const employee = employees.find((employee) => employee.EmployeeName === salesQuote.ownerContact)
    if (employee) {
      const user: User = {
        id: employee.EmployeeID || "",
        displayName: employee.EmployeeName || "",
        email: employee.ContactInfo?.Email || "",
      }
      return user
    }
    return currentUser
  }

  const convertOwnerEmployeeNameToUser = (ownerEmployeeName: string): User => {
    const employee = employees.find((employee) => employee.EmployeeName === ownerEmployeeName)
    let user: User = emptyPerson
    if (employee) {
      user = {
        id: employee.EmployeeID || "",
        displayName: employee.EmployeeName || "",
        email: employee.ContactInfo?.Email || "",
      }
    }
    return user
  }

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true)
        setError(null)
        await Promise.all([
          fetchProjects({ fresh: false }),
          fetchLeads({ fresh: false }),
          fetchCustomers({ fresh: false }),
          fetchEmployees({ fresh: false }),
        ])
      } catch (err) {
        console.error("Error loading data:", err)
        setError("Fehler beim Laden der Projektdaten. Bitte erneut versuchen.")
        setSnackbar({ message: (err as Error)?.message || "Unbekannter Fehler", severity: "error" })
      } finally {
        setLoading(false)
      }
    }
    loadData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const projectValue = projects.find((pro) => {
      return pro?.lead?.opportunityID === projectID
    })
    if (projectValue) {
      // Enrich management and sales User data from employees if only ID is present
      const enrichedProject = { ...projectValue }

      // Enrich management field
      if (
        projectValue.management?.id &&
        !projectValue.management?.displayName &&
        employees.length > 0
      ) {
        const managementEmployee = employees.find(
          (emp) =>
            emp.id === projectValue.management.id || emp.EmployeeID === projectValue.management.id
        )
        if (managementEmployee) {
          enrichedProject.management = {
            id: projectValue.management.id,
            displayName: managementEmployee.EmployeeName || "",
            email: managementEmployee.ContactInfo?.Email || "",
            userPrincipalName: managementEmployee.ContactInfo?.Email || "",
          }
        }
      }

      // Enrich sales field
      if (projectValue.sales?.id && !projectValue.sales?.displayName && employees.length > 0) {
        const salesEmployee = employees.find(
          (emp) => emp.id === projectValue.sales.id || emp.EmployeeID === projectValue.sales.id
        )
        if (salesEmployee) {
          enrichedProject.sales = {
            id: projectValue.sales.id,
            displayName: salesEmployee.EmployeeName || "",
            email: salesEmployee.ContactInfo?.Email || "",
            userPrincipalName: salesEmployee.ContactInfo?.Email || "",
          }
        }
      }
      setProject(enrichedProject)

      const lead = leads.find((lead) => lead.opportunityID === projectID)
      if (lead) {
        setCurrentLead(lead)
      }
    } else {
      const lead = leads.find((lead) => lead.opportunityID === projectID)
      if (lead) {
        const newProject: Project = {
          ...emptyProject,
          lead: lead,
          id: "NEW",
          sales: convertOwnerEmployeeNameToUser(lead.ownerEmployeeName || ""),
          name: lead.subject || "Neuer Projektname",
          desiredDate: "",
          plannedDate: "",
        }
        setProject(newProject)
        setIsEditing(false)
        setEditTitle(lead.subject || "Neuer Projektname")
        setCurrentLead(lead)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectID, projects, employees])

  useEffect(() => {
    if (salesQuote && (!project.management?.id || project.management?.id === "")) {
      // Only set from salesQuote if no management is set at all
      setProject((prev) => ({
        ...prev,
        management: getPersonBySalesQuote(salesQuote),
        sales: getPersonBySalesQuote(salesQuote),
      }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [salesQuote])

  useEffect(() => {
    if (project.lead.opportunityID) {
      const fetchSalesQuotes = async () => {
        const grouped = await getSalesQuotesByOpportunityIDs({
          opportunityIDs: [project.lead.opportunityID || ""],
          fresh: true,
        })
        const quotes = grouped && grouped[0] ? grouped[0] : []
        // if the value of primary is true, set the salesQuote
        const primaryQuote = quotes.find((quote: SalesQuote) => quote.primary)
        if (primaryQuote) {
          setSalesQuote(primaryQuote)
        }
      }
      fetchSalesQuotes()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project.lead.opportunityID])

  // Handle navigation from ExaminationPage with device selection
  useEffect(() => {
    if (location.state?.activeTab !== undefined) {
      setCurrentTab(location.state.activeTab)
    }
    if (location.state?.selectedDeviceId && project.devices) {
      const device = project.devices.find((d) => d.id === location.state.selectedDeviceId)
      if (device) {
        setSelectedDevice(device)
        // Switch to devices tab if needed
        if (location.state?.activeTab === undefined) {
          setCurrentTab(0) // EUT/Aux tab
        }
      }
    }
  }, [location.state, project.devices])

  const handleSaveGeneral = async (name: string, value: string) => {
    setIsUpdating(true)
    const newProject = {
      ...project,
      updatedDate: new Date().toISOString(),
      [name]: value,
    }
    setProject(newProject)
    await updateProject(newProject)
    setIsUpdating(false)
  }

  const handleSaveProject = async (project: Project) => {
    setIsUpdating(true)
    setProject(project)
    await updateProject(project)
    setIsUpdating(false)
  }

  const handleDeviceClick = (id: string) => {
    const device = project.devices.find((device) => device.id === id)
    if (device) {
      setSelectedDevice(device)
    }
  }
  //return in json format
  // console.log(JSON.stringify(project, null, 2))

  // Helper function to find the next available device number
  const getNextDeviceNumber = () => {
    const usedNumbers = new Set<number>()

    // Extract all device numbers from existing barcodes
    project.devices.forEach((device) => {
      const barcodeParts = device.barcode.split("-")
      if (barcodeParts.length >= 2) {
        const deviceNumber = parseInt(barcodeParts[1])
        if (!isNaN(deviceNumber)) {
          usedNumbers.add(deviceNumber)
        }
      }
    })

    // Find the first available number starting from 1
    let nextNumber = 1
    while (usedNumbers.has(nextNumber)) {
      nextNumber++
    }

    return nextNumber
  }

  const handleDeviceCopy = (id: string) => {
    const device = project.devices.find((device) => device.id === id)
    let newDevice: Device | DeviceEutGroup | null = null
    const nextDeviceNumber = getNextDeviceNumber()

    if (device && isEutGroup(device)) {
      newDevice = {
        ...device,
        examinations: device.examinations.map((exam) => ({
          ...exam,
          id: v4(),
          status: Status.OPEN, // Reset examination status to OPEN
          salesQuote: "",
          calculation:
            exam.norm.examinationCalculations.find((ec) => ec.name === exam.name)?.calculation ||
            exam.examinationConfig.defaultCalculation, // Use calculation from norm, fallback to config default
          // Reset testDatas for each task to prevent copying test data
          tasks: exam.tasks.map((task) => ({
            ...task,
            testDatas: [], // Reset testDatas to empty array
            estimatedTime: 0,
            workingTime: 0,
            status: Status.OPEN,
            examinator: emptyPerson,
          })),
          // Reset other examination-specific data
          history: [],
          deviceSetup: [],
          specialArrangements: "",
          // Reset operatingModes to use deviceDataGroup values (treat as new device)
          operatingModes: getOperatingModes(device),
        })),
        barcode: `${project.lead.opportunityID}-${nextDeviceNumber}`,
        name: `${device.name} (Kopie)`,
        id: "NEW",
        devices: device.devices.map((d, index) => ({
          ...d,
          id: v4(),
          barcode: `${project.lead.opportunityID}-${nextDeviceNumber}-${index + 1}`,
        })),
      }
    }
    if (device && isDeviceAux(device)) {
      newDevice = {
        ...device,
        barcode: `${project.lead.opportunityID}-${nextDeviceNumber}`,
        name: `${device.name} (Kopie)`,
        id: "NEW",
        movementData: [],
      }
    }
    setSelectedDevice(newDevice)
  }

  const handleAddDevice = (type: DeviceType) => {
    const nextDeviceNumber = getNextDeviceNumber()

    if (type === DeviceType.EUT_GROUP) {
      const newDevice: DeviceEutGroup = {
        id: "NEW",
        type: DeviceType.EUT_GROUP,
        name: "",
        barcode: `${project.lead.opportunityID}-${nextDeviceNumber}`,
        description: "",
        deviceDataGroup: [],
        images: [],
        documents: [],
        assumptions: "",
        movementData: [],
        notes: [],
        devices: [
          {
            id: v4(),
            type: DeviceType.EQUIPMENT_UNDER_TEST,
            barcode: `${project.lead.opportunityID}-${nextDeviceNumber}-1`,
            name: "",
            description: "",
            images: [],
            documents: [],
            note: [],
            movementData: [],
          },
        ],
        examinations: [],
        norms: [],
      }
      setSelectedDevice(newDevice)
    }
    if (type === DeviceType.AUX) {
      const newDevice: DeviceAux = {
        id: "NEW",
        type: DeviceType.AUX,
        name: "",
        barcode: `${project.lead.opportunityID}-${nextDeviceNumber}`,
        description: "",
        images: [],
        documents: [],
        deviceDataGroup: [],
        movementData: [],
      }
      setSelectedDevice(newDevice)
    }
  }

  const handleDeviceSave = async (
    deviceToSave: Device | DeviceEutGroup,
    isOffer: boolean = false,
    noSnackbar: boolean = false
  ) => {
    if (deviceToSave) {
      setIsUpdating(true)
      try {
        let newDevicesArray: Device[]
        if (deviceToSave.id === "NEW") {
          deviceToSave.id = v4()
          newDevicesArray = [...project.devices, deviceToSave]
        } else {
          newDevicesArray = project.devices.map((device) =>
            device.id === deviceToSave.id ? deviceToSave : device
          )
        }

        const updatedProject = {
          ...project,
          devices: newDevicesArray,
          updatedDate: new Date().toISOString(),
        }

        if (isEutGroup(deviceToSave)) {
          // Persist device first so EUT changes survive even if examinations fail
          await updateEutGroup({ deviceEutGroup: deviceToSave, isOffer: isOffer })

          // Then try to sync examinations; do not block device save on errors
          if (deviceToSave.examinations) {
            for (const exam of deviceToSave.examinations) {
              try {
                await updateExamination(exam)
              } catch (e) {
                console.warn("Failed to update examination after device save", e)
              }
            }
          }
        }
        if (isDeviceAux(deviceToSave)) {
          await updateAux(deviceToSave)
        }

        setProject(updatedProject)
        await updateProject({ ...updatedProject, noSnackbar: noSnackbar })
        setSelectedDevice(null)
        await deleteUnusedExaminations()
      } finally {
        setIsUpdating(false)
      }
    }
  }

  const handleDeviceDelete = async (ids: string[]) => {
    setIsUpdating(true)
    try {
      const updatedDevices = project.devices.filter((device) => !ids.includes(device.id))
      const updatedProject = {
        ...project,
        devices: updatedDevices,
        updatedDate: new Date().toISOString(),
      }
      setProject(updatedProject)
      await updateProject(updatedProject)
    } finally {
      setIsUpdating(false)
    }
  }

  const [shouldRefreshProject, setShouldRefreshProject] = useState(false)

  const handleProjectRefresh = async () => {
    try {
      setIsUpdating(true)
      // Set flag to trigger refresh
      setShouldRefreshProject(true)
      // Fetch fresh projects from backend
      await fetchProjects({ fresh: true })
    } catch (error) {
      console.error("Error refreshing projects:", error)
    } finally {
      setIsUpdating(false)
    }
  }

  // Effect to update project when projects are refreshed
  useEffect(() => {
    if (shouldRefreshProject) {
      const updatedProjectData = projects.find((pro) => {
        return pro?.lead?.opportunityID === projectID
      })

      if (updatedProjectData) {
        // Update the local project state with fresh data
        setProject(updatedProjectData)
      }
      setShouldRefreshProject(false)
    }
  }, [projects, projectID, shouldRefreshProject])

  const getBusinessCustomerName = (opportunityID: string) => {
    const lead = leads.find((lead) => lead.opportunityID === opportunityID)
    if (lead) {
      const customer = customers.find((customer) => customer.customerID === lead.businessAccount)
      if (customer) {
        return customer.customerName
      }
    }
    return ""
  }

  const getBusinessAccount = (opportunityID: string) => {
    const lead = leads.find((lead) => lead.opportunityID === opportunityID)
    if (lead) {
      return lead.businessAccount || ""
    }
    return ""
  }

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "50vh",
        }}
      >
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          justifyContent: "center",
          alignItems: "center",
          height: "50vh",
        }}
      >
        <Typography variant="h6" color="error">
          {error}
        </Typography>
        <StyledButton
          buttonType="custom"
          customText="Erneut versuchen"
          onClick={async () => {
            try {
              setError(null)
              setLoading(true)
              await Promise.all([
                fetchProjects({ fresh: true }),
                fetchLeads({ fresh: true }),
                fetchCustomers({ fresh: true }),
                fetchEmployees({ fresh: true }),
              ])
            } catch (err) {
              setError("Fehler beim Laden der Projektdaten. Bitte erneut versuchen.")
              setSnackbar({
                message: (err as Error)?.message || "Unbekannter Fehler",
                severity: "error",
              })
            } finally {
              setLoading(false)
            }
          }}
        />
      </Box>
    )
  }

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue)
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        gap: 2,
        height: "100%",
        width: "100%",
      }}
    >
      <Box sx={{ flex: 1 }}>
        <Card>
          <Box sx={{ position: "relative" }}>
            <CardHeader
              title={project.name === "" ? "Bitte Projektnamen eingeben" : project.name}
              subheader={
                <Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Box>
                      <Typography sx={{ mb: 0.5 }}>
                        {getBusinessCustomerName(project.lead.opportunityID || "")}
                      </Typography>
                      <Typography>
                        <a
                          href={`https://treo.haufe-x360.app/(W(7))/Main?CompanyID=${HAUFE_COMPANY_ID}&ScreenId=CR304000&OpportunityID=${project.lead.opportunityID}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {project.lead.opportunityID}
                        </a>
                        {" - "}
                        {salesQuote?.quoteNbr && (
                          <a
                            href={`https://treo.haufe-x360.app/(W(7))/Main?CompanyID=${HAUFE_COMPANY_ID}&ScreenId=CR304500&OpportunityID=${project.lead.opportunityID}&QuoteNbr=${salesQuote.quoteNbr}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {salesQuote.quoteNbr}
                          </a>
                        )}
                      </Typography>
                    </Box>
                    <Typography>{`${project.updatedDate ? `Letzte Aktualisierung: ${formatDateTime(project.updatedDate)}` : ""}`}</Typography>
                  </Box>
                </Box>
              }
              action={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  {!isEditing && (
                    <IconButton
                      onClick={() => {
                        setIsEditing(true)
                        setEditTitle(project.name || "Neuer Projektname")
                      }}
                    >
                      <Edit />
                    </IconButton>
                  )}
                </Box>
              }
            />

            {isEditing && (
              <Paper
                elevation={3}
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  p: 1,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <TextField
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  placeholder={editTitle}
                  autoFocus
                  fullWidth
                  slotProps={{
                    input: {
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => {
                              handleSaveGeneral("name", editTitle)
                              setEditTitle("")
                              setIsEditing(false)
                            }}
                            edge="end"
                          >
                            <Save />
                          </IconButton>

                          <IconButton
                            onClick={() => {
                              setIsEditing(false)
                              setEditTitle("")
                            }}
                            edge="end"
                          >
                            <Cancel />
                          </IconButton>
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              </Paper>
            )}
          </Box>

          {currentLead && (
            <ProjectDetailGeneralData
              project={project}
              onSave={handleSaveProject}
              onSaveDevices={handleDeviceSave}
              lead={currentLead}
              businessAccount={getBusinessAccount(project.lead.opportunityID || "")}
              onTestSetupSave={async (updatedProject: Project) => {
                if (testSetupPlanTabRef.current) {
                  // First save the project data to backend
                  await handleSaveProject(updatedProject)
                  // Then save TestSetup changes
                  await testSetupPlanTabRef.current.handleSaveAllChanges()
                  // Update the project state with the latest changes
                  setProject(updatedProject)
                  setHasTestSetupChanges(false)
                }
              }}
              onTestSetupCancel={() => {
                if (testSetupPlanTabRef.current) {
                  testSetupPlanTabRef.current.handleCancelAllChanges()
                  setHasTestSetupChanges(false)
                }
              }}
              hasTestSetupChanges={() => hasTestSetupChanges}
            />
          )}
        </Card>
      </Box>
      <Box sx={{ flex: 1 }}>
        {project.id !== "NEW" ? (
          <>
            <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
              <Tabs value={currentTab} onChange={handleTabChange}>
                <Tab label="EUT/Aux" />
                <Tab label="Prüfplan" />
              </Tabs>
            </Box>

            {currentTab === 0 && (
              <ProjectDeviceList
                project={project}
                onEdit={handleDeviceClick}
                onAdd={handleAddDevice}
                onDelete={handleDeviceDelete}
                onCopy={handleDeviceCopy}
              />
            )}

            {currentTab === 1 && (
              <TestSetupPlanTab
                ref={testSetupPlanTabRef}
                project={project}
                onSave={handleDeviceSave}
                onProjectRefresh={handleProjectRefresh}
                projectSalesQuote={salesQuote?.quoteNbr || ""}
                onChangesStateChange={setHasTestSetupChanges}
              />
            )}

            {selectedDevice && (
              <Dialog open={!!selectedDevice} maxWidth={"xl"} fullWidth>
                {(isDeviceEut(selectedDevice) ||
                  isEutGroup(selectedDevice) ||
                  isDeviceAux(selectedDevice)) && (
                  <DeviceDetails
                    otherDevices={project.devices.filter(
                      (device) => device.id !== selectedDevice.id
                    )}
                    device={selectedDevice}
                    project={project}
                    onChange={setSelectedDevice}
                    onCancel={() => setSelectedDevice(null)}
                    onSave={handleDeviceSave}
                  />
                )}
              </Dialog>
            )}
          </>
        ) : (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
            }}
          >
            <Typography>
              Das Projekt muss erst gespeichert werden, bevor ein EUT hinzugefügt werden kann.
            </Typography>
          </Box>
        )}
      </Box>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar !== null}
        autoHideDuration={6000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        {snackbar ? (
          <Alert
            onClose={() => setSnackbar(null)}
            severity={snackbar.severity}
            sx={{ width: "100%" }}
          >
            {snackbar.message}
          </Alert>
        ) : undefined}
      </Snackbar>

      {/* Loading Backdrop */}
      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.modal + 1,
          position: "absolute",
          backgroundColor: "rgba(0, 0, 0, 0.7)",
        }}
        open={isUpdating}
      >
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
          <CircularProgress color="inherit" />
          <Typography variant="h6">Aktualisiere Daten...</Typography>
        </Box>
      </Backdrop>
    </Box>
  )
}
